import React, { useEffect, useRef, useCallback } from 'react';

/* ─── Theme tokens (synced with AgentCircuitLoader) ─── */
const PRIMARY = '#003A79';
const PRIMARY_DARK = '#003A79';
const ACCENT = '#d4007e';

/* ─── Animation ─── */
const ANIM_DURATION_MS = 1400;
const PER_NODE_ANIM = 0.25;
const WAVE_SPREAD = 0.7;
const EDGE_DELAY = 0.04;
const PACKET_FADE_MS = 150;

/* ─── Colour helpers ─── */
const rgb = (hex: string) => {
  const n = Number.parseInt(hex.replace('#', ''), 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
};
const pC = rgb(PRIMARY);
const pCDark = rgb(PRIMARY_DARK);
const aC = rgb(ACCENT);

const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);

/* ─── Seeded PRNG (mulberry32) for stable layout across reloads ─── */
function mulberry32(seed: number) {
  return () => {
    seed = Math.trunc(seed);
    seed = Math.trunc(seed + 0x6d2b79f5);
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/* ─── Min distance to nearest of the four corners ─── */
function minCornerDist(x: number, y: number, vw: number, vh: number): number {
  return Math.min(
    Math.hypot(x, y),
    Math.hypot(vw - x, y),
    Math.hypot(x, vh - y),
    Math.hypot(vw - x, vh - y),
  );
}

interface GraphNode {
  x: number;
  y: number;
  t: number;
  phase: number;
}

interface GraphEdge {
  i: number;
  j: number;
  t: number;
}

interface DataPacket {
  path: number[];
  speed: number;
  phase: number;
}

/* ─── Build graph with seeded randomness ─── */
function buildGraph(vw: number, vh: number): { nodes: GraphNode[]; edges: GraphEdge[]; packets: DataPacket[]; faces: number[][] } {
  const rng = mulberry32(42);
  const nodes: GraphNode[] = [];
  const diag = Math.hypot(vw, vh);
  const spacing = Math.max(24, Math.min(48, diag * 0.034));
  const margin = spacing * 2;
  const cols = Math.ceil((vw + margin * 2) / spacing);
  const rows = Math.ceil((vh + margin * 2) / spacing);
  const jitter = spacing * 0.35;

  for (let c = -1; c <= cols; c++) {
    for (let r = -1; r <= rows; r++) {
      const jx = (rng() - 0.5) * 2 * jitter;
      const jy = (rng() - 0.5) * 2 * jitter;
      const x = -margin + c * spacing + jx;
      const y = -margin + r * spacing + jy;
      const dist = minCornerDist(x, y, vw, vh);
      nodes.push({ x, y, t: dist, phase: rng() * Math.PI * 2 });
    }
  }

  const maxDist = Math.max(...nodes.map((n) => n.t), 1);
  for (const n of nodes) n.t /= maxDist;

  const connectRadius = spacing * 1.6;
  const edges: GraphEdge[] = [];
  for (let i = 0; i < nodes.length; i++) {
    for (let j = i + 1; j < nodes.length; j++) {
      const dx = nodes[j].x - nodes[i].x;
      const dy = nodes[j].y - nodes[i].y;
      if (dx * dx + dy * dy <= connectRadius * connectRadius) {
        edges.push({ i, j, t: Math.max(nodes[i].t, nodes[j].t) });
      }
    }
  }

  // Build adjacency: for each node, list of neighbor node indices (sorted by angle for face traversal)
  const adj: number[][] = Array.from({ length: nodes.length }, () => []);
  for (const e of edges) {
    adj[e.i].push(e.j);
    adj[e.j].push(e.i);
  }
  for (let i = 0; i < nodes.length; i++) {
    adj[i].sort((a, b) => {
      const ax = Math.atan2(nodes[a].y - nodes[i].y, nodes[a].x - nodes[i].x);
      const bx = Math.atan2(nodes[b].y - nodes[i].y, nodes[b].x - nodes[i].x);
      return ax - bx;
    });
  }

  // Find closed faces (polygons): right-hand rule traversal from each directed edge; dedupe by canonical cycle
  const faceSet = new Set<string>();
  const maxFaceLen = 14;
  for (const e of edges) {
    const path = [e.i, e.j];
    let prev = e.i;
    let cur = e.j;
    for (let step = 0; step < maxFaceLen; step++) {
      const list = adj[cur];
      const idx = list.indexOf(prev);
      if (idx === -1) break;
      const next = list[(idx - 1 + list.length) % list.length];
      path.push(next);
      if (next === e.i) {
        if (path.length >= 3 && path.length <= maxFaceLen) {
          const minIdx = path.indexOf(Math.min(...path));
          const canon = [...path.slice(minIdx), ...path.slice(0, minIdx)].join(',');
          faceSet.add(canon);
        }
        break;
      }
      prev = cur;
      cur = next;
    }
  }
  const faces = Array.from(faceSet).map((s) => s.split(',').map(Number));

  // Each packet walks a seeded random path through 12-20 nodes (no immediate backtrack)
  // Start nodes are evenly spaced across the node list so packets scatter uniformly
  const packetCount = Math.max(40, Math.min(80, Math.round(edges.length * 0.25)));
  const stride = nodes.length / packetCount;
  const packets: DataPacket[] = [];
  for (let p = 0; p < packetCount; p++) {
    const pathLen = 12 + Math.floor(rng() * 9);
    let cur = Math.floor(p * stride + rng() * stride * 0.5) % nodes.length;
    const path = [cur];
    let prev = -1;
    for (let step = 1; step < pathLen; step++) {
      const neighbors = adj[cur].filter((n) => n !== prev);
      if (neighbors.length === 0) break;
      const next = neighbors[Math.floor(rng() * neighbors.length)];
      path.push(next);
      prev = cur;
      cur = next;
    }
    packets.push({
      path,
      speed: 0.9 + rng() * 0.8,
      phase: rng(),
    });
  }

  return { nodes, edges, packets, faces };
}

/* ─── Colour at horizontal position ─── */
function colorAt(mx: number, pc: { r: number; g: number; b: number } = pC): { r: number; g: number; b: number } {
  return {
    r: Math.round(pc.r + (aC.r - pc.r) * mx),
    g: Math.round(pc.g + (aC.g - pc.g) * mx),
    b: Math.round(pc.b + (aC.b - pc.b) * mx),
  };
}

/* ═══════════════════════════ Component ═══════════════════════════ */
export const KnowledgeGraphBackground: React.FC<{ isDark?: boolean }> = ({ isDark = false }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const graphRef = useRef<ReturnType<typeof buildGraph>>({ nodes: [], edges: [], packets: [], faces: [] });
  const rafRef = useRef(0);
  const startRef = useRef(0);
  const doneRef = useRef(false);

  const draw = useCallback((progress: number, elapsed: number) => {
    const primaryRgb = isDark ? pCDark : pC;
    const cvs = canvasRef.current;
    if (!cvs) return;
    const ctx = cvs.getContext('2d', { alpha: true });
    if (!ctx) return;
    const dpr = Math.max(window.devicePixelRatio || 1, 1);
    const w = cvs.width / dpr;
    const h = cvs.height / dpr;

    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, cvs.width, cvs.height);

    const { nodes, edges, packets, faces } = graphRef.current;
    if (!nodes.length) return;
    const nodeRadius = Math.max(1.2, Math.min(2.8, Math.min(w, h) * 0.004));
    const glowRadius = nodeRadius * 3.5;
    const lineWidth = Math.max(0.4, Math.min(1, Math.min(w, h) * 0.0015));

    // Closed faces: position-aware very light gradient (left→right matches viewport; before edges)
    for (const face of faces) {
      if (face.length < 3) continue;
      const pts = face.map((idx) => nodes[idx]);
      const maxT = Math.max(...face.map((idx) => nodes[idx].t));
      const faceAppear = maxT * WAVE_SPREAD + EDGE_DELAY;
      if (progress < faceAppear) continue;
      const lp = Math.min((progress - faceAppear) / PER_NODE_ANIM, 1);
      const fillAlpha = easeOutCubic(lp) * 0.07;
      const cy = pts.reduce((s, p) => s + p.y, 0) / pts.length;
      const xMin = Math.min(...pts.map((p) => p.x));
      const xMax = Math.max(...pts.map((p) => p.x));
      const xMid = (xMin + xMax) / 2;
      const c0 = colorAt(Math.max(0, Math.min(xMin / w, 1)), primaryRgb);
      const cMid = colorAt(Math.max(0, Math.min(xMid / w, 1)), primaryRgb);
      const c1 = colorAt(Math.max(0, Math.min(xMax / w, 1)), primaryRgb);
      const grad = ctx.createLinearGradient(xMin, cy, xMax, cy);
      grad.addColorStop(0, `rgba(${c0.r},${c0.g},${c0.b},${fillAlpha})`);
      grad.addColorStop(0.5, `rgba(${cMid.r},${cMid.g},${cMid.b},${fillAlpha * 0.95})`);
      grad.addColorStop(1, `rgba(${c1.r},${c1.g},${c1.b},${fillAlpha})`);
      ctx.beginPath();
      ctx.moveTo(pts[0].x, pts[0].y);
      for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i].x, pts[i].y);
      ctx.closePath();
      ctx.fillStyle = grad;
      ctx.fill();
    }

    // Edges: gradient lines with draw-in (staggered after nodes)
    for (const edge of edges) {
      const nodeAppear = edge.t * WAVE_SPREAD;
      const edgeAppear = nodeAppear + EDGE_DELAY;
      if (progress < edgeAppear) continue;
      const lp = Math.min((progress - edgeAppear) / PER_NODE_ANIM, 1);
      const alpha = easeOutCubic(lp) * 0.35;
      const drawIn = easeOutCubic(lp);
      const a = nodes[edge.i];
      const b = nodes[edge.j];
      const bx = a.x + (b.x - a.x) * drawIn;
      const by = a.y + (b.y - a.y) * drawIn;
      const mxA = Math.max(0, Math.min(a.x / w, 1));
      const mxB = Math.max(0, Math.min(bx / w, 1));
      const cA = colorAt(mxA, primaryRgb);
      const cB = colorAt(mxB, primaryRgb);
      const grad = ctx.createLinearGradient(a.x, a.y, bx, by);
      grad.addColorStop(0, `rgba(${cA.r},${cA.g},${cA.b},${alpha})`);
      grad.addColorStop(1, `rgba(${cB.r},${cB.g},${cB.b},${alpha})`);
      ctx.beginPath();
      ctx.moveTo(a.x, a.y);
      ctx.lineTo(bx, by);
      ctx.strokeStyle = grad;
      ctx.lineWidth = lineWidth;
      ctx.lineCap = 'round';
      ctx.stroke();
    }

    // Nodes: soft glow + dot + subtle pulse
    const pulseTime = elapsed / 1000;
    for (const node of nodes) {
      const appearAt = node.t * WAVE_SPREAD;
      if (progress < appearAt) continue;
      const lp = Math.min((progress - appearAt) / PER_NODE_ANIM, 1);
      const baseAlpha = easeOutCubic(lp);
      const pulse = lp >= 1 ? 1 + 0.15 * Math.sin(pulseTime * 1.2 + node.phase) : 1;
      const alpha = baseAlpha * pulse;
      const mx = Math.max(0, Math.min(node.x / w, 1));
      const c = colorAt(mx, primaryRgb);

      // Soft radial glow
      const glow = ctx.createRadialGradient(node.x, node.y, 0, node.x, node.y, glowRadius);
      glow.addColorStop(0, `rgba(${c.r},${c.g},${c.b},${alpha * 0.18})`);
      glow.addColorStop(1, `rgba(${c.r},${c.g},${c.b},0)`);
      ctx.beginPath();
      ctx.arc(node.x, node.y, glowRadius, 0, Math.PI * 2);
      ctx.fillStyle = glow;
      ctx.fill();

      // Dot
      ctx.beginPath();
      ctx.arc(node.x, node.y, nodeRadius * pulse, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(${c.r},${c.g},${c.b},${alpha * 0.7})`;
      ctx.fill();
      ctx.strokeStyle = `rgba(${c.r},${c.g},${c.b},${alpha * 0.4})`;
      ctx.lineWidth = lineWidth;
      ctx.stroke();
    }

    // Data packets: dots traversing multi-node paths after graph is complete (smooth fade-in, no delay)
    if (progress >= 1) {
      const timeSinceComplete = elapsed - ANIM_DURATION_MS;
      const packetAlpha = Math.min(1, timeSinceComplete / PACKET_FADE_MS);
      const packetRadius = nodeRadius * 1.4;
      const timeSec = elapsed / 1000;
      for (const pkt of packets) {
        const segments = pkt.path.length - 1;
        if (segments < 1) continue;
        const raw = ((timeSec * pkt.speed + pkt.phase) % segments);
        const segIdx = Math.min(Math.floor(raw), segments - 1);
        const segT = raw - segIdx;
        const a = nodes[pkt.path[segIdx]];
        const b = nodes[pkt.path[segIdx + 1]];
        const px = a.x + (b.x - a.x) * segT;
        const py = a.y + (b.y - a.y) * segT;
        const mx = Math.max(0, Math.min(px / w, 1));
        const c = colorAt(mx, primaryRgb);

        const glow = ctx.createRadialGradient(px, py, 0, px, py, packetRadius * 3);
        glow.addColorStop(0, `rgba(${c.r},${c.g},${c.b},${0.35 * packetAlpha})`);
        glow.addColorStop(1, `rgba(${c.r},${c.g},${c.b},0)`);
        ctx.beginPath();
        ctx.arc(px, py, packetRadius * 3, 0, Math.PI * 2);
        ctx.fillStyle = glow;
        ctx.fill();

        ctx.beginPath();
        ctx.arc(px, py, packetRadius, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${c.r},${c.g},${c.b},${0.9 * packetAlpha})`;
        ctx.fill();
      }
    }
  }, [isDark]);

  const resize = useCallback(() => {
    const cvs = canvasRef.current;
    if (!cvs) return;
    const dpr = Math.max(window.devicePixelRatio || 1, 1);
    const rect = cvs.getBoundingClientRect();
    cvs.width = Math.ceil(rect.width * dpr);
    cvs.height = Math.ceil(rect.height * dpr);
    graphRef.current = buildGraph(rect.width, rect.height);
    if (doneRef.current) draw(1, performance.now());
  }, [draw]);

  useEffect(() => {
    resize();
    window.addEventListener('resize', resize);
    const frame = (ts: number) => {
      if (!startRef.current) startRef.current = ts;
      const elapsed = ts - startRef.current;
      const p = Math.min(elapsed / ANIM_DURATION_MS, 1);
      draw(p, elapsed);
      if (p < 1) {
        rafRef.current = requestAnimationFrame(frame);
      } else {
        doneRef.current = true;
        rafRef.current = requestAnimationFrame(frame);
      }
    };
    rafRef.current = requestAnimationFrame(frame);
    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(rafRef.current);
    };
  }, [resize, draw]);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden
      tabIndex={-1}
      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }}
    />
  );
};

export default KnowledgeGraphBackground;